import React, { useEffect, useState } from "react";
import { Card, message } from "antd";

import CustomTableComponents from "../CommonComponents/CustomTableComponents";
import { useDispatch, useSelector } from "react-redux";
import ImageComponent from "../CommonComponents/ImageComponent/ImageComponent";
import CustomAvatar from "../CommonComponents/CustomAvatar";
import {
  changeUserStatus,
  deleteUser,
  getAllUsersByRole,
} from "../../redux/userSlice";
import { style } from "../../Styles";
import { MinusOutlined } from "@ant-design/icons";
import DrawerUser from "./UserDrawer";
import { getSpanStyleForRole } from "../../Utils/Style/style-depending-on-role";
import { enumToUserFriendlyText } from "../../Utils/Imports/UserFriendlyEnum";
import Header from "../../global/Header/Header";
import ActionDropdownToChangeStatus from "../CommonComponents/ActionDropdownToChangeStatus";
import { useTranslation } from "react-i18next";

const KioskAndValidatorUsers = ({ type }) => {
  const primaryColor = process.env.REACT_APP_PRIMARY_COLOR;
  const { t } = useTranslation();
  const columns = [
    {
      title: t("kioskAndValidatorUsersScreen.table.fullName"),
      key: "fullname",
      render: (record) => {
        return (
          <div style={{ display: "flex", alignItems: "center" }}>
            {record.profilePicture ? (
              <ImageComponent size={40} imageId={record?.profilePicture} />
            ) : (
              <CustomAvatar
                name={`${record.name} ${record.surname}`}
                type="initials"
              />
            )}
            <span style={{ marginLeft: 8 }}>
              {record?.name ? (
                `${record.name} ${record.surname}`
              ) : (
                <MinusOutlined />
              )}
            </span>
          </div>
        );
      },
      sorter: (a, b) => {
        const nameA = `${a?.name || ""} ${a?.surname || ""}`;
        const nameB = `${b?.name || ""} ${b?.surname || ""}`;
        return nameA.localeCompare(nameB);
      },
      width: 250,
      show: true,
    },
    {
      title: t("kioskAndValidatorUsersScreen.table.roles"),
      key: "roles",
      render: (record) => {
        return record?.roles ? (
          record?.roles?.map((role) => (
            <span style={style.dataContainer}>
              <div style={getSpanStyleForRole(role)}>
                {enumToUserFriendlyText(role)}
              </div>
            </span>
          ))
        ) : (
          <MinusOutlined />
        );
      },
      sorter: (a, b) => {
        const rolesA = a?.roles || [];
        const rolesB = b?.roles || [];
        const lengthCompare = rolesA?.length - rolesB?.length;

        // If the lengths are the same, compare the roles lexicographically
        if (lengthCompare === 0) {
          return rolesA?.join(",").localeCompare(rolesB?.join(","));
        }

        // Otherwise, sort by the length of the roles array
        return lengthCompare;
      },
      show: true,
    },
    {
      title: t("kioskAndValidatorUsersScreen.table.relatedMachineId"),
      key: "relatedMachineId",
      render: (record) => {
        return record?.relatedMachineId ? (
          <span>{record.relatedMachineId}</span>
        ) : (
          <MinusOutlined />
        );
      },
      show: true,
    },
    {
      title: t("kioskAndValidatorUsersScreen.table.status"),
      key: "active",
      render: (record) => {
        return (
          <span style={style.dataContainer}>
            {record.active ? (
              <div style={style.statusActif}>{t("statuses.active")}</div>
            ) : (
              <div style={style.statusInactif}>{t("statuses.inactive")}</div>
            )}
          </span>
        );
      },
      sorter: (a, b) => a.active.length - b.active.length,
      show: true,
    },
    {
      title: t("kioskAndValidatorUsersScreen.table.actions"),
      key: "actions",
      render: (record) => {
        return (
          <ActionDropdownToChangeStatus
            record={record}
            onChangeStatus={onChangeStatus}
            recordName={`${record.name} ${record.surname}`}
          />
        );
      },
      show: true,
    },
  ];
  const onChangeStatus = (ObjToSend) => {
    dispatch(changeUserStatus(ObjToSend))
      .unwrap()
      .then(() => {
        message.success(t("userScreen.messages.userStatusUpdatedSuccessfully"));
      })
      .catch(() => {
        message.error("Error changing user status");
      });
  };

  const [filter, setFilter] = useState("");

  const [open, setOpen] = useState(false);
  const [user, setUser] = useState(null);
  const [isMakingApiCall, setMakingApiCall] = useState(true);

  const [csvHeaders, setCsvHeaders] = useState([
    {
      label: "Full name",
      key: "fullName",
    },
    {
      label: "Roles",
      key: "roles",
    },
    {
      label: "Related Machine Id",
      key: "relatedMachineId",
    },
    {
      label: "Status",
      key: "active",
    },

  ]);
  const [csvData, setCsvData] = useState([]);

  const showDrawer = () => {
    setOpen(true);
  };
  const onClose = () => {
    setOpen(false);
    setUser(null);
  };

  const dispatch = useDispatch();

  const UserDependingOnReactComponent = () => {
    switch (type) {
      case "KioskUser":
        return {
          addButtonText: t("KioskUser.addButtonText"),
          searchPlaceholder: t("KioskUser.searchPlaceholder"),
          dispatchAction: getAllUsersByRole({ role: "KIOSK_ROLE" }),
          subTitle: t("KioskUser.subTitle"),
          type: "KioskUser",
          role: "KIOSK_ROLE",
        };

      case "ValidatorUser":
        return {
          addButtonText: t("ValidatorUser.addButtonText"),
          searchPlaceholder: t("ValidatorUser.searchPlaceholder"),
          dispatchAction: getAllUsersByRole({ role: "VALIDATOR_ROLE" }),
          subTitle: t("ValidatorUser.subTitle"),
          type: "ValidatorUser",
          role: "VALIDATOR_ROLE",
        };
      default:
        return;
    }
  };

  const { users } = useSelector((state) => state.users);
  const fetchAll = () => {
    dispatch(UserDependingOnReactComponent().dispatchAction)
      .unwrap()
      .then()
      .catch(() => {
        message.error("Error fetching users");
      })
      .finally(() => {
        setMakingApiCall(false);
      });
  };

  useEffect(() => {
    fetchAll();
  }, [dispatch, type]);

  let usersArray = Object.values(users.usersList);
  usersArray = usersArray.map((item) => {
    return { ...item, key: item.id };
  });
  const filtredData = usersArray.filter((item) => {
    return (
      item?.id?.toString().toLowerCase().includes(filter.toLowerCase()) ||
      item?.name?.toString().toLowerCase().includes(filter.toLowerCase()) ||
      item?.surname?.toString().toLowerCase().includes(filter.toLowerCase())
    );
  });

  const handleSelectedUser = (selectedUser) => {
    setUser(selectedUser);
    showDrawer();
  };

  const handleDeleteUser = (id, callback) => {
    setMakingApiCall(true);
    dispatch(
      deleteUser({
        userId: id,
      })
    )
      .unwrap()
      .then(() => {
        message.success("User deleted successfully");
        callback();
      })
      .catch(() => {})
      .finally(() => {
        onClose();
        setMakingApiCall(false);
      });
  };

  const createdCsvData = filtredData.map((item) => {
    return {...item, 
      fullName: item.name +" "+ item.surname, 
      phone: item.phoneCountryCode+ " " + item.phone,
      roles: item.roles?.map((role) => {return enumToUserFriendlyText(role) + " "}),
    }
  })
  
  const handleGenerateCsv = (event, done) => {
    const promise = async () => {

      const createdCsvData = filtredData.map((item) => {
        return {...item, 
          fullName: item.name +" "+ item.surname, 
          phone: item.phoneCountryCode+ " " + item.phone,
          roles: item.roles?.map((role) => {return enumToUserFriendlyText(role) + " "}),
        }
      })
      setCsvData(createdCsvData)
      setCsvHeaders([
        {
          label: "Full name",
          key: "fullName",
        },
        {
          label: "Roles",
          key: "roles",
        },
        {
          label: "Related Machine Id",
          key: "relatedMachineId",
        },
        {
          label: "Status",
          key: "active",
        },

      ]);
    };

    promise().then(done());
  };

  return (
    <>
      <Header
        addButtonText={UserDependingOnReactComponent()?.addButtonText}
        onAddButtonClick={() => showDrawer()}
        onSearch={(e) => {
          setFilter(e.target.value);
        }}
        searchPlaceholder={UserDependingOnReactComponent()?.searchPlaceholder}
        addButtonBackgroundColor={primaryColor} // #ecefff
        addButtonBorderColor={primaryColor}
        addButtonTextColor={"#ffffff"}
        onReload={fetchAll}
        showAddButton={true}
        BreadcrumbItems={[
          {
            title: "Users",
          },
          {
            title: UserDependingOnReactComponent()?.subTitle,
          },
        ]}
        showBreadcrumb={true}
        showExportCSV={true}
        csvHeaders={csvHeaders}
        csvData={createdCsvData}
        generateCSVfn={handleGenerateCsv}
        csvDataMiddleFileName={"Users"}
      />
      <Card style={{ margin: 16, height: "85vh" }}>
        <CustomTableComponents
          isMakingApiCall={users?.loading}
          lo
          columns={columns.filter((column) => column.show)}
          dataArray={filtredData}
          handleDoubleClickRow={(record) => handleSelectedUser(record)}
          handleSelectedRow={() => {}}
        />
      </Card>
      {open && (
        <DrawerUser
          onClose={onClose}
          open={open}
          userId={user?.id}
          type={UserDependingOnReactComponent()?.type}
          role={UserDependingOnReactComponent()?.role}
          translatedType={UserDependingOnReactComponent()?.subTitle}
        />
      )}
    </>
  );
};

export default KioskAndValidatorUsers;
